import type { FpdConfig, FpdDay, FpdRequest, FpdStatus, FpdYear, Scope } from '../../core/types'

/**
 * Flex PD's pure logic: which school year a date is in, which flex days a
 * person sees, the month grid. No Firebase here, so it is tested
 * (tests/flexpd.test.ts).
 */

/** School years run Aug 1 to Jul 31 unless the district sets other dates. */
export function schoolYearOf(d: Date = new Date()): Pick<FpdYear, 'id' | 'label' | 'start' | 'end'> {
  const y = d.getMonth() >= 7 ? d.getFullYear() : d.getFullYear() - 1
  const next = String((y + 1) % 100).padStart(2, '0')
  return { id: `${y}-${next}`, label: `${y}–${next}`, start: `${y}-08-01`, end: `${y + 1}-07-31` }
}

/** The year after this one, for setting next year's thresholds ahead of time. */
export function nextYearOf(id: string): Pick<FpdYear, 'id' | 'label' | 'start' | 'end'> {
  const y = Number(id.slice(0, 4)) + 1
  return schoolYearOf(new Date(y, 8, 1))
}

/** Today as an ISO date in local time (never UTC: a 9pm save is still today). */
export function isoToday(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * The flex days someone sees: those for any building they work in, or every
 * day for someone whose access is district-wide. Sorted by date.
 */
export function daysFor(days: FpdDay[], scopes: Scope[] | 'all'): FpdDay[] {
  const mine = scopes === 'all' || scopes.includes('district') ? days
    : days.filter((d) => d.buildings.includes('district') || d.buildings.some((b) => scopes.includes(b)))
  return [...mine].sort((a, b) => a.date.localeCompare(b.date))
}

/** Days still to come (today included), open first. */
export function upcoming(days: FpdDay[], today: string = isoToday()): FpdDay[] {
  return days.filter((d) => d.date >= today)
}

export const BUILDING_SCOPES: Scope[] = ['hs', 'ms', 'es']

/** "Every building", or the buildings by name. */
export function buildingsLabel(b: Scope[], labels: Record<string, string>): string {
  if (b.includes('district') || BUILDING_SCOPES.every((s) => b.includes(s))) return 'Every building'
  return b.map((s) => labels[s] ?? s).join(', ')
}

/** Weeks of a month, Sunday first, padded with the neighbouring months' days. */
export function monthGrid(year: number, month0: number): Array<Array<{ iso: string; day: number; inMonth: boolean }>> {
  const first = new Date(year, month0, 1)
  const start = new Date(year, month0, 1 - first.getDay())
  const weeks: Array<Array<{ iso: string; day: number; inMonth: boolean }>> = []
  const cur = new Date(start)
  do {
    const week = []
    for (let i = 0; i < 7; i++) {
      week.push({ iso: isoToday(cur), day: cur.getDate(), inMonth: cur.getMonth() === month0 })
      cur.setDate(cur.getDate() + 1)
    }
    weeks.push(week)
  } while (cur.getMonth() === month0)
  return weeks
}

/** What is wrong with a year's thresholds, in words, or null. */
export function thresholdProblem(total: number | null, collab: number | null): string | null {
  if (total === null && collab === null) return null
  if (total !== null && (!Number.isFinite(total) || total < 0 || total > 200)) return 'Total hours must be between 0 and 200.'
  if (collab !== null && (!Number.isFinite(collab) || collab < 0)) return 'Collaborative hours cannot be negative.'
  if (total !== null && collab !== null && collab > total) return 'Collaborative hours cannot be more than the total.'
  return null
}

/** Where a district starts before anyone edits the lists. Every entry can be renamed or retired. */
export const DEFAULT_CONFIG: FpdConfig = {
  focus: [
    { name: 'Literacy' }, { name: 'Math' }, { name: 'Student engagement' },
    { name: 'Assessment and grading' }, { name: 'Technology' }, { name: 'Social-emotional learning' },
  ],
  places: [{ name: 'In my building' }, { name: 'Another district building' }, { name: 'Off-site' }, { name: 'Virtual' }],
  sendBack: [
    { name: 'Needs more detail' }, { name: 'Date is not an eligible flex day' },
    { name: 'Does not map to a PD goal' }, { name: 'Hours look off' },
  ],
}

/* ---------------- requests ---------------- */


export const hoursOf = (r: Pick<FpdRequest, 'startMin' | 'endMin'>) => Math.max(0, r.endMin - r.startMin) / 60

/** "1.5 hours", "1 hour". */
export function hoursLabel(h: number): string {
  const n = Math.round(h * 100) / 100
  return `${n} ${n === 1 ? 'hour' : 'hours'}`
}

/** "08:15" ⇄ minutes after midnight. */
export const toMin = (hhmm: string) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm)
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN
}
export const fromMin = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
export function clock(min: number): string {
  const h = Math.floor(min / 60), m = min % 60
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`
}

export type RequestForm = Pick<FpdRequest, 'building' | 'date' | 'startMin' | 'endMin' | 'place' | 'focus' | 'title'
  | 'description' | 'collaborative' | 'collaborators' | 'retroactive' | 'evidence'>

/** What the form still needs before it can be sent, in words. Empty = ready. */
export function formGaps(f: RequestForm, today: string = isoToday()): string[] {
  return [
    !f.date && 'a date',
    !(f.endMin > f.startMin) && 'a start and end time',
    !f.focus.trim() && 'a learning focus',
    !f.place.trim() && 'a place',
    !f.title.trim() && 'a title',
    !f.description.trim() && 'what you will do',
    f.collaborative && f.collaborators.length === 0 && 'who you are working with',
    f.retroactive && !f.evidence.trim() && 'what you did (evidence)',
    f.retroactive && !!f.date && f.date > today && 'a date that has passed (it is retroactive)',
  ].filter(Boolean) as string[]
}

/** Is the date one of the building's open flex days? A request off the calendar is flagged, not refused. */
export function onCalendar(date: string, building: Scope, days: FpdDay[]): boolean {
  return days.some((d) => !d.closed && d.date === date && (d.buildings.includes('district') || d.buildings.includes(building)))
}

/** The status a teacher's send moves to: approval first, or straight to verifying. */
export function sendStatus(r: Pick<FpdRequest, 'retroactive' | 'evidence'>, from: FpdStatus): 'submitted' | 'completed' {
  if (r.retroactive) return 'completed'
  // Sent back at the verifying stage: it already happened, so it goes back to be verified.
  return from === 'returned' && r.evidence.trim() ? 'completed' : 'submitted'
}

/** Where a request is, for whoever is reading it. Generated per viewer, never stored. */
export function statusText(r: Pick<FpdRequest, 'status' | 'retroactive'>, viewer: 'teacher' | 'principal'): string {
  const t = viewer === 'teacher'
  switch (r.status) {
    case 'draft': return t ? 'Draft, only you can see it' : 'Draft'
    case 'submitted': return t ? 'Waiting on your principal' : 'Waiting on you to approve'
    case 'returned': return t ? 'Sent back: see the note' : 'Sent back to the teacher'
    case 'approved': return t ? 'Approved: after the day, add what you did' : 'Approved, not done yet'
    case 'denied': return 'Denied'
    case 'completed': return t ? 'Done: waiting on your principal to verify' : 'Waiting on you to verify'
    case 'verified': return 'Verified: hours count'
  }
}

/** The pill tone for a status: colour always ships with the words. */
export function statusTone(s: FpdStatus): 'good' | 'serious' | 'critical' | 'plain' {
  return s === 'verified' ? 'good' : s === 'returned' ? 'serious' : s === 'denied' ? 'critical' : 'plain'
}

/**
 * A teacher's year: verified hours are banked; approved, submitted and
 * completed are planned (asked for, not yet counted). Collaborative banked
 * hours count toward the collaborative requirement.
 */
export function totals(rs: FpdRequest[]): { banked: number; planned: number; collabBanked: number } {
  let banked = 0, planned = 0, collabBanked = 0
  for (const r of rs) {
    const h = hoursOf(r)
    if (r.status === 'verified') { banked += h; if (r.collaborative) collabBanked += h }
    else if (r.status === 'approved' || r.status === 'submitted' || r.status === 'completed') planned += h
  }
  return { banked, planned, collabBanked }
}

/** Waiting on a principal: to approve, or to verify. */
export const waitingOnPrincipal = (r: Pick<FpdRequest, 'status'>) => r.status === 'submitted' || r.status === 'completed'

/* ---------------- the hours tracker ---------------- */

export interface TrackerRow {
  email: string
  name: string
  banked: number
  collabBanked: number
  planned: number
  /** Hours still to verify against the requirement (never below zero). */
  needed: number
  collabNeeded: number
  /** Learning focus of every request that counts or is planned, most hours first. */
  focuses: string[]
  requests: FpdRequest[]
}

/**
 * One row per teacher, furthest behind first: hours still needed, then
 * collaborative hours still needed, then name. Requests from people no longer
 * listed as teachers still show, so nothing anyone did goes missing.
 */
export function trackerRows(
  teachers: Array<{ email: string; name: string }>, requests: FpdRequest[], total: number | null, collab: number | null,
): TrackerRow[] {
  const by = new Map<string, FpdRequest[]>()
  for (const r of requests) if (r.status !== 'draft') by.set(r.teacherEmail, [...(by.get(r.teacherEmail) ?? []), r])
  const people = new Map(teachers.map((t) => [t.email, t.name]))
  for (const [email, rs] of by) if (!people.has(email)) people.set(email, rs[0]!.teacherName)
  const rows = [...people].map(([email, name]): TrackerRow => {
    const rs = by.get(email) ?? []
    const t = totals(rs)
    const hoursBy = new Map<string, number>()
    for (const r of rs) if (r.status !== 'denied' && r.status !== 'returned') hoursBy.set(r.focus, (hoursBy.get(r.focus) ?? 0) + hoursOf(r))
    return {
      email, name, ...t, requests: rs,
      needed: total == null ? 0 : Math.max(0, total - t.banked),
      collabNeeded: collab == null ? 0 : Math.max(0, collab - t.collabBanked),
      focuses: [...hoursBy].sort((a, b) => b[1] - a[1]).map(([f]) => f),
    }
  })
  return rows.sort((a, b) => b.needed - a.needed || b.collabNeeded - a.collabNeeded || a.name.localeCompare(b.name))
}

/** Hours toward each learning focus (verified and planned): "how much of our PD went to literacy". */
export function focusHours(requests: FpdRequest[]): Array<{ focus: string; banked: number; planned: number }> {
  const m = new Map<string, { banked: number; planned: number }>()
  for (const r of requests) {
    const e = m.get(r.focus) ?? { banked: 0, planned: 0 }
    if (r.status === 'verified') e.banked += hoursOf(r)
    else if (r.status === 'approved' || r.status === 'submitted' || r.status === 'completed') e.planned += hoursOf(r)
    else continue
    m.set(r.focus, e)
  }
  return [...m].map(([focus, v]) => ({ focus, ...v })).sort((a, b) => b.banked + b.planned - (a.banked + a.planned))
}

/* ---------------- the bell ---------------- */

export interface FpdAlert { id: string; n: number; text: string; to: string }

/**
 * What a person has to act on in Flex PD, for the bell. Only actions: a
 * request sent back, an approved day that has passed with nothing said about
 * it, and (for principals) requests to approve or verify. Approvals and
 * verifications themselves do not ring: they show on the teacher's list, and
 * a bell full of good news trains people to ignore it.
 */
export function flexAlerts(mine: FpdRequest[], waitingOnMe: number, today: string = isoToday()): FpdAlert[] {
  const out: FpdAlert[] = []
  const back = mine.filter((r) => r.status === 'returned')
  if (back.length === 1) out.push({ id: 'fpd-back', n: 1, text: `Your request “${back[0]!.title || 'Untitled'}” was sent back: see the note`, to: `/flex-pd/${back[0]!.id}` })
  else if (back.length > 1) out.push({ id: 'fpd-back', n: back.length, text: `${back.length} of your requests were sent back: see the notes`, to: '/flex-pd' })
  const due = mine.filter((r) => r.status === 'approved' && r.date <= today).sort((a, b) => a.date.localeCompare(b.date))
  for (const r of due) {
    const day = new Date(r.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    out.push({ id: `fpd-done-${r.id}`, n: 1, text: `Add what you did on ${day}, so the hours count`, to: `/flex-pd/${r.id}` })
  }
  if (waitingOnMe > 0) out.push({ id: 'fpd-review', n: waitingOnMe, text: `${waitingOnMe} Flex PD ${waitingOnMe === 1 ? 'request' : 'requests'} to approve or verify`, to: '/flex-pd/review' })
  return out
}
